import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { NotificationsOffOutlined } from '@mui/icons-material';
import {
  fetchPreferenceSetting,
  disableUserPreference,
  enableUserPreference,
} from '../../../../helpers/api';
import { openNotification } from '../../../../helpers/notification';
import { AppContext } from '../../../../store/AppContext';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import '../style.css';

const isTrue = (value) => String(value).trim().toLowerCase() === 'true';

// makeApiCall rejects "no records" responses; treat those as an empty list
const isEmptyResult = (error) => {
  const status = error?.result?.status || error?.data?.result?.status;
  return status?.code === '03' || /empty|no record|not found/i.test(status?.desc || '');
};

const errorMessage = (error, fallback) =>
  error?.result?.status?.desc ||
  error?.data?.result?.status?.desc ||
  (typeof error === 'string' ? error : error?.message) ||
  fallback;

const ModifyPreferences = () => {
  // Use the signed-in user from the session, so this works for every user category
  const { state } = useContext(AppContext);
  const userId = state?.auth?.result?.data?.userId;

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState({}); // { [preferenceName]: true }

  const loadPreferences = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      setLoadError('We could not identify your account. Please sign in again.');
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      const res = await fetchPreferenceSetting(userId);
      const data = res?.result?.data;
      setGroups(Array.isArray(data) ? data : []);
    } catch (error) {
      setGroups([]);
      if (!isEmptyResult(error)) {
        setLoadError(errorMessage(error, "Couldn't load your preferences. Please try again."));
      }
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  const setPreferenceValue = (preferenceName, value) =>
    setGroups((prev) =>
      prev.map((group) => ({
        ...group,
        content: (group.content || []).map((item) =>
          item.preferenceName === preferenceName ? { ...item, preferenceValue: String(value) } : item
        ),
      }))
    );

  const handleToggle = async (preferenceName, checked) => {
    // Update straight away, roll back if the server refuses
    setPreferenceValue(preferenceName, checked);
    setSaving((prev) => ({ ...prev, [preferenceName]: true }));
    try {
      await (checked ? enableUserPreference : disableUserPreference)(userId, preferenceName);
    } catch (error) {
      setPreferenceValue(preferenceName, !checked);
      openNotification({
        type: 'error',
        message: errorMessage(error, "Couldn't save that preference. Please try again."),
      });
    } finally {
      setSaving((prev) => ({ ...prev, [preferenceName]: false }));
    }
  };

  if (loading) {
    return (
      <Box sx={{ py: 2 }}>
        <LoaderSkeleton />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Alert
        severity="warning"
        sx={{ borderRadius: '12px' }}
        action={
          <Button color="inherit" size="small" onClick={loadPreferences}>
            Retry
          </Button>
        }
      >
        {loadError}
      </Alert>
    );
  }

  if (!groups.length) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            mx: 'auto',
            mb: 2,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha('#0c5bab', 0.08),
            color: '#0c5bab',
          }}
        >
          <NotificationsOffOutlined />
        </Box>
        <Typography variant="subtitle1" fontWeight={700}>
          No preferences to set yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          There are no notification settings available for your account at the moment.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="notification__form">
      {groups.map((group, index) => (
        <Paper
          key={group.title || index}
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            mb: 2.5,
            borderRadius: '16px',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: alpha('#F4F6F8', 0.4),
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {group.title}
            </Typography>
            {group.subTitle && (
              <Typography variant="body2" color="text.secondary">
                {group.subTitle}
              </Typography>
            )}
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Stack spacing={1.5}>
            {(group.content || []).map((item) => {
              const isDisabled = String(item.isActive).trim().toUpperCase() === 'FALSE';
              const isSaving = Boolean(saving[item.preferenceName]);
              return (
                <Stack
                  key={item.preferenceName}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={2}
                  sx={{
                    p: 1.5,
                    borderRadius: '10px',
                    bgcolor: isDisabled ? alpha('#000', 0.02) : '#fff',
                    border: '1px solid',
                    borderColor: alpha('#000', 0.05),
                    opacity: isDisabled ? 0.7 : 1,
                  }}
                >
                  <Tooltip
                    title={isDisabled ? 'This preference has been turned off by the system administrator' : ''}
                    placement="top-start"
                  >
                    <Typography variant="body2" color={isDisabled ? 'text.disabled' : 'text.primary'}>
                      {item.preferenceDesc || item.preferenceTitle || item.preferenceName}
                    </Typography>
                  </Tooltip>
                  {isSaving ? (
                    <CircularProgress size={18} sx={{ mx: 1.5 }} />
                  ) : (
                    <Switch
                      size="small"
                      disabled={isDisabled}
                      checked={isTrue(item.preferenceValue)}
                      onChange={(e) => handleToggle(item.preferenceName, e.target.checked)}
                      inputProps={{ 'aria-label': item.preferenceDesc || item.preferenceName }}
                    />
                  )}
                </Stack>
              );
            })}
          </Stack>
        </Paper>
      ))}
    </Box>
  );
};

export default ModifyPreferences;
